/**
 * Country translation utility
 * 
 * Database stores country names in English as the canonical reference.
 * This utility provides translated display names based on the current language.
 */

// English to French country name mapping
const COUNTRY_TRANSLATIONS: Record<string, Record<string, string>> = {
  en: {
    // Countries stay as-is in English
  },
  fr: {
    'Australia': 'Australie',
    'Austria': 'Autriche',
    'Belgium': 'Belgique',
    'Brazil': 'Brésil',
    'Canada': 'Canada',
    'China': 'Chine',
    'Costa Rica': 'Costa Rica',
    'Croatia': 'Croatie',
    'Denmark': 'Danemark',
    'Dubai': 'Dubaï',
    'Egypt': 'Égypte',
    'Finland': 'Finlande',
    'France': 'France',
    'Germany': 'Allemagne',
    'Greece': 'Grèce',
    'Hungary': 'Hongrie',
    'Iceland': 'Islande',
    'India': 'Inde',
    'Indonesia': 'Indonésie',
    'Ireland': 'Irlande',
    'Italy': 'Italie',
    'Japan': 'Japon',
    'Malaysia': 'Malaisie',
    'Mexico': 'Mexique',
    'Morocco': 'Maroc',
    'Netherlands': 'Pays-Bas',
    'New Zealand': 'Nouvelle-Zélande',
    'Norway': 'Norvège',
    'Philippines': 'Philippines',
    'Poland': 'Pologne',
    'Portugal': 'Portugal',
    'Romania': 'Roumanie',
    'Singapore': 'Singapour',
    'South Africa': 'Afrique du Sud',
    'South Korea': 'Corée du Sud',
    'Spain': 'Espagne',
    'Sweden': 'Suède',
    'Switzerland': 'Suisse',
    'Thailand': 'Thaïlande',
    'Turkey': 'Turquie',
    'United Arab Emirates': 'Émirats arabes unis',
    'United Kingdom': 'Royaume-Uni',
    'United States': 'États-Unis',
    'Vietnam': 'Vietnam',
  },
  de: {
    'Australia': 'Australien',
    'Austria': 'Österreich',
    'Belgium': 'Belgien',
    'Brazil': 'Brasilien',
    'Canada': 'Kanada',
    'China': 'China',
    'Costa Rica': 'Costa Rica',
    'Croatia': 'Kroatien',
    'Denmark': 'Dänemark',
    'Dubai': 'Dubai',
    'Egypt': 'Ägypten',
    'Finland': 'Finnland',
    'France': 'Frankreich',
    'Germany': 'Deutschland',
    'Greece': 'Griechenland',
    'Hungary': 'Ungarn',
    'Iceland': 'Island',
    'India': 'Indien',
    'Indonesia': 'Indonesien',
    'Ireland': 'Irland',
    'Italy': 'Italien',
    'Japan': 'Japan',
    'Malaysia': 'Malaysia',
    'Mexico': 'Mexiko',
    'Morocco': 'Marokko',
    'Netherlands': 'Niederlande',
    'New Zealand': 'Neuseeland',
    'Norway': 'Norwegen',
    'Philippines': 'Philippinen',
    'Poland': 'Polen',
    'Portugal': 'Portugal',
    'Romania': 'Rumänien',
    'Singapore': 'Singapur',
    'South Africa': 'Südafrika',
    'South Korea': 'Südkorea',
    'Spain': 'Spanien',
    'Sweden': 'Schweden',
    'Switzerland': 'Schweiz',
    'Thailand': 'Thailand',
    'Turkey': 'Türkei',
    'United Arab Emirates': 'Vereinigte Arabische Emirate',
    'United Kingdom': 'Vereinigtes Königreich',
    'United States': 'Vereinigte Staaten',
    'Vietnam': 'Vietnam',
  },
  es: {
    'Australia': 'Australia',
    'Austria': 'Austria',
    'Belgium': 'Bélgica',
    'Brazil': 'Brasil',
    'Canada': 'Canadá',
    'China': 'China',
    'Costa Rica': 'Costa Rica',
    'Croatia': 'Croacia',
    'Denmark': 'Dinamarca',
    'Dubai': 'Dubái',
    'Egypt': 'Egipto',
    'Finland': 'Finlandia',
    'France': 'Francia',
    'Germany': 'Alemania',
    'Greece': 'Grecia',
    'Hungary': 'Hungría',
    'Iceland': 'Islandia',
    'India': 'India',
    'Indonesia': 'Indonesia',
    'Ireland': 'Irlanda',
    'Italy': 'Italia',
    'Japan': 'Japón',
    'Malaysia': 'Malasia',
    'Mexico': 'México',
    'Morocco': 'Marruecos',
    'Netherlands': 'Países Bajos',
    'New Zealand': 'Nueva Zelanda',
    'Norway': 'Noruega',
    'Philippines': 'Filipinas',
    'Poland': 'Polonia',
    'Portugal': 'Portugal',
    'Romania': 'Rumania',
    'Singapore': 'Singapur',
    'South Africa': 'Sudáfrica',
    'South Korea': 'Corea del Sur',
    'Spain': 'España',
    'Sweden': 'Suecia',
    'Switzerland': 'Suiza',
    'Thailand': 'Tailandia',
    'Turkey': 'Turquía',
    'United Arab Emirates': 'Emiratos Árabes Unidos',
    'United Kingdom': 'Reino Unido',
    'United States': 'Estados Unidos',
    'Vietnam': 'Vietnam',
  },
};

/**
 * Get translated country name for display
 * @param countryName - English country name (from database)
 * @param language - Target language code (en, fr, de, es)
 * @returns Translated country name or original if no translation exists
 */
export function getCountryDisplayName(countryName: string | null | undefined, language: string = 'en'): string {
  if (!countryName) return '';
  
  // If language is English or not supported, return as-is
  if (language === 'en' || !COUNTRY_TRANSLATIONS[language]) {
    return countryName;
  }
  
  // Return translation or original
  return COUNTRY_TRANSLATIONS[language][countryName] || countryName;
}

/**
 * Get canonical English name from any language variant
 * Useful for reverse lookup when user selects in their language
 */
export function getCanonicalCountryName(displayName: string, language: string = 'en'): string {
  if (language === 'en' || !COUNTRY_TRANSLATIONS[language]) {
    return displayName;
  }
  
  const translations = COUNTRY_TRANSLATIONS[language];
  for (const [english, translated] of Object.entries(translations)) {
    if (translated.toLowerCase() === displayName.toLowerCase()) {
      return english;
    }
  }
  
  return displayName;
}

/**
 * Hook-friendly wrapper to use with react-i18next
 */
export function useCountryTranslation() {
  return {
    getDisplayName: getCountryDisplayName,
    getCanonicalName: getCanonicalCountryName,
  };
}
